import React, { useEffect, useRef, useState } from "react";
import startCase from "lodash/startCase";
import { useSpeechRecognition } from "react-speech-recognition";
import SpeechRecognition from "react-speech-recognition";

import {
  Box,
  IconButton,
  Avatar,
  Typography,
  Button,
  Stack,
  useMediaQuery,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";

import { TextField, Paper, InputAdornment } from "@mui/material";

import SendIcon from "@mui/icons-material/Send";

import "./ChatAssistant.css";

import voiceAssistantImg from "assets/app/voice-assistant.png";
import keyboardImg from "assets/app/keyboard.png";

import { useVoiceAssistantState } from "context/VoiceAssistantContext";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MOBILE_MAX_WIDTH_768,
  voiceAssistantPath,
  MOBILE_MAX_LISTED_RESULT,
} from "constants/appConstants";
import { useDispatch, useSelector } from "react-redux";
import { getProperties, getPropertyData } from "selectors/propertySelectors";
import { getProjectData } from "selectors/projectSelector";
import { getjobData } from "selectors/jobSelectors";
import {
  getAgentProcessing,
  getGlobalSearch,
  getUserProfileDetails,
} from "selectors/appSelector";

import { setPropertyData } from "actions/propertyActions";
import DataGridTable from "components/Table/DataGrid";
import {
  setBuilderAiCommandQuestionsInitialData,
  chatWithOpenAiAction,
} from "actions/appBuilderAICommandActions";
import { INITIAL_FLOW_STATE } from "./VoiceAssistantCommandExecutionFlow";

import ListeningAnimation from "components/VoiceAssistantLayout/ListeningAnimation/ListeningAnimation";

import {
  hasSubscription,
  subscriptionConstants as plans,
} from "core/utils/roleUtils";

const columns = (props) => [
  {
    field: "projectName",
    headerName: "Name",
    sortable: false,
    width: 150,
    resizable: false,
    renderCell: (rowData) => {
      return (
        <Tooltip title={rowData.formattedValue}>
          <p
            className="link-text"
            style={{ height: "100%", alignContent: "center" }}
            onClick={() => {
              const selectedProperty = props.propertiesList.find(
                (property) => property.propertyId === rowData.row.propertyId
              );

              props.setPropertyData(selectedProperty);
            }}
          >
            {rowData.row.propertyName}
          </p>
        </Tooltip>
      );
    },
  },
  {
    field: "property",
    headerName: "Address",
    resizable: false,
    width: 200,
    renderCell: (rowData) => {
      return (
        <p style={{ height: "100%", alignContent: "center" }}>
          {rowData.row.address}
        </p>
      );
    },
  },
];

// Note: We keep chat state only in-component to preserve full bot history.

const itemsColumnsByType = {
  properties: ["propertyName", "address", "homeownerName"],
};

const showArrowBack = false;

const VoiceAssistantLayout = () => {
  const chatBodyRef = useRef(null);

  const dispatch = useDispatch();
  const chatWithOpenAi = chatWithOpenAiAction(dispatch);

  const {
    VoiceAssistantState,
    setVoiceAssistantState,
    voiceAssistantEnabled,
    voiceAssistantConfig,
    VoiceAssistantFlowState,
    setVoiceAssistantFlowState,
    voiceAssistantMicrophoneAccess,
  } = useVoiceAssistantState();

  const [isTyping, setIsTyping] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const inputRef = useRef(null);

  const [selectedTab, updateTab] = useState("overview");

  const MAX_AWAIT_TIME_ELAPSED_SECONDS = 1;
  const isMobile = useMediaQuery(MOBILE_MAX_WIDTH_768);

  const location = useLocation();
  const navigate = useNavigate();

  const { pathname } = location;

  const [chatMessages, setChatMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const endOfMessagesRef = useRef(null); // sentinel for auto-scroll

  const { propertiesList, totalCount } = useSelector(getProperties);
  const appRoleDetails = useSelector(getUserProfileDetails);

  // const propertyData = useSelector(getPropertyData);

  // const projectData = useSelector(getProjectData);
  // const jobData = useSelector(getjobData);

  const userProfile = useSelector(getUserProfileDetails);
  const agentProcessing = useSelector(getAgentProcessing);

  const isFreeSubscription = hasSubscription(
    userProfile,
    plans.FREE_SUBSCRIPTION
  );

  const handleUserTyping = () => {
    setIsTyping(true);
    const timeout = setTimeout(() => setIsTyping(false), 2000);
    return () => clearTimeout(timeout);
  };

  const clearSession = () => {
    try {
      handleVoiceCommand(false);
      resetTranscript();
      resetTranscript();
    } catch (e) {}
    setChatSession(null);
    setChatMessages([]);
    dispatch(setBuilderAiCommandQuestionsInitialData());
    setVoiceAssistantFlowState(INITIAL_FLOW_STATE);
    setVoiceAssistantState({ ...VoiceAssistantState, isListening: false });
  };

  const handleInputSend = () => {
    if (inputMessage && inputMessage.trim()) {
      handleSend(inputMessage);
      setInputMessage("");
    }
  };

  const handleSend = async (
    text,
    clickEnabled = false,
    messageType = "text",
    data = [],
    takeAction = true
  ) => {
    const textStr = typeof text === "string" ? text : String(text);
    if (!textStr.trim()) {
      return;
    }

    const normalized = textStr.trim().toLowerCase();
    const clearPhrases = new Set([
      "reset",
      "clear",
      "clear session",
      "clear chat",
      "reset chat",
      "start over",
      "new chat",
    ]);
    if (clearPhrases.has(normalized)) {
      clearSession();
      return;
    }

    const userMessage = {
      sender: "user",
      text: textStr,
      timestamp: Date.now(),
      clickEnabled,
      messageType,
      data,
    };

    setChatMessages((prev) => [...prev, userMessage]);

    if (!takeAction) {
      await delay(1000);
      return;
    }

    console.log("Check the Text of input ", textStr);
    handleCommandExecutionApiCall(textStr);

    // dispatch(fetchBuilderAiCommandQuestions(currentCommand.commandId));
  };

  const keyPress = (e) => {
    handleUserTyping();

    if (e.keyCode === 13) {
      handleInputSend();
    }
  };

  const [message, setMessage] = useState("");
  const [chatSession, setChatSession] = useState(null);

  let commands = [
    {
      command: "reset",
      callback: () => {
        clearSession();
      },
    },
    {
      command: "shut up",
      callback: () => setMessage("I wasn't talking."),
    },
    {
      command: "Stop",
      callback: () => {
        clearSession();
      },
    },
    {
      command: "Cancel",
      callback: () => {
        clearSession();
      },
    },
    {
      command: "clear session",
      callback: () => clearSession(),
    },
    {
      command: "clear chat",
      callback: () => clearSession(),
    },
    {
      command: "reset chat",
      callback: () => clearSession(),
    },
    {
      command: "start over",
      callback: () => clearSession(),
    },
    {
      command: "Hello",
      callback: () => setMessage("Hi there!"),
    },
    {
      command: "Create Contact",
      callback: () => {
        setVoiceAssistantState({
          ...VoiceAssistantState,
          isListening: false,
        });
        // dependencies
        // check if customer is present in the system
      },
    },
    {
      command: "check if customer is present in the system",
      callback: () => {
        setVoiceAssistantState({
          ...VoiceAssistantState,
          isListening: false,
        });

        setMessage("Checking in the system");
        speak("Checking in the system");
      },
    },
  ];

  const [typewriterText, setTypewriterText] = useState("");

  const [lastCalled, setLastCalled] = useState(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const intervalRef = useRef(null);

  const {
    finalTranscript,
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition({ commands });

  const handleVoiceCommand = (startListening) => {
    if (startListening) {
      SpeechRecognition.startListening({
        language: "en-US",
        continuous: true,
      });
    } else {
      SpeechRecognition.stopListening();
    }
  };

  // typewriterEffect()
  useEffect(() => {
    let currentIndex = -1;
    setTypewriterText("");

    const typewriterEffect = () => {
      handleUserTyping();

      if (currentIndex < finalTranscript.length) {
        setTypewriterText((prev) => {
          setLastCalled(Date.now());

          if (
            typeof prev !== "undefined" &&
            typeof finalTranscript[currentIndex] !== "undefined"
          ) {
            return prev + finalTranscript[currentIndex];
          }

          if (typeof prev !== "undefined") {
            return prev;
          }

          if (typeof finalTranscript[currentIndex] !== "undefined") {
            return finalTranscript[currentIndex];
          }
        });

        currentIndex++;
        setTimeout(typewriterEffect, 70);
      }
    };

    typewriterEffect();
  }, [finalTranscript]);

  // voice recognition
  useEffect(() => {
    if (lastCalled !== null) {
      // Clear any existing interval
      if (intervalRef.current) clearInterval(intervalRef.current);

      // Start a new interval to update elapsed time
      intervalRef.current = setInterval(() => {
        handleUserTyping();

        const timeElapsed = Math.floor((Date.now() - lastCalled) / 1000);

        setTimeElapsed(timeElapsed);

        if (timeElapsed >= MAX_AWAIT_TIME_ELAPSED_SECONDS) {
          handleVoiceCommand(false);
          setVoiceAssistantState({
            ...VoiceAssistantState,
            isListening: false,
          });

          console.log("Final Transcript", finalTranscript);
          if (
            VoiceAssistantFlowState.command !==
            finalTranscript.toLocaleLowerCase()
          ) {
            handleSend(finalTranscript);
          }

          setTypewriterText("");
          resetTranscript();

          setTimeout(() => {
            handleVoiceCommand(false);
            clearInterval(intervalRef.current);
          }, 500);
        }
      }, 1000);

      // Cleanup interval on unmount
      return () => {
        clearInterval(intervalRef.current);
      };
    }
  }, [lastCalled]);

  // voiceAssistantMicrophoneAccess
  useEffect(() => {
    console.log(
      "VoiceAssistantLayout:useEffect:voiceAssistantMicrophoneAccess",
      voiceAssistantMicrophoneAccess
    );

    // if (!voiceAssistantMicrophoneAccess) {
    //   dispatch(checkMicrophonePermissions());
    // }

    if (VoiceAssistantState.isListening) {
      handleVoiceCommand(true);
      handleUserTyping();
      handleKeyboardClick(false);
      setShowInput(false);
    } else {
      handleVoiceCommand(false);
    }
  }, [
    VoiceAssistantState,
    VoiceAssistantState.isListening,
    dispatch,
    voiceAssistantMicrophoneAccess,
  ]);

  const [isAutoScroll, setIsAutoScroll] = useState(true);

  const handleScroll = () => {
    if (chatBodyRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatBodyRef.current;
      setIsAutoScroll(scrollTop + clientHeight >= scrollHeight - 10);
    }
  };

  // auto scroll when new messages, typing indicator or processing state changes
  useEffect(() => {
    if (!isAutoScroll) return;

    // If the latest message is a suggestions list (and potentially tall),
    // skip auto-scroll so user sees the beginning/top options instead of
    // being forced to the bottom of the list.
    const lastMessage = chatMessages[chatMessages.length - 1];
    if (
      lastMessage &&
      Array.isArray(lastMessage.suggestions) &&
      lastMessage.suggestions.length > 0
    ) {
      return; // prevent jump to bottom for suggestions batch
    }

    if (endOfMessagesRef.current) {
      // smooth scroll only if user near bottom; otherwise jump to bottom when very first messages
      endOfMessagesRef.current.scrollIntoView({ behavior: "smooth" });
    } else if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [chatMessages, agentProcessing, isTyping, isAutoScroll]);

  // showInput - inputRef for keyboard
  useEffect(() => {
    if (showInput) {
      inputRef.current?.focus();
      handleVoiceCommand(false);
      setVoiceAssistantState({
        ...VoiceAssistantState,
        isListening: false,
      });
    }
  }, [showInput]);

  // onDestroy - setVoiceAssistantState:false
  useEffect(() => {
    return () => {
      handleVoiceCommand(false);

      setVoiceAssistantFlowState(INITIAL_FLOW_STATE);
      dispatch(setBuilderAiCommandQuestionsInitialData());

      setVoiceAssistantState({
        ...VoiceAssistantState,
        isListening: false,
      });
    };
  }, []);

  if (!browserSupportsSpeechRecognition) {
    return <span>Browser doesn't support speech recognition.</span>;
  }

  const speak = (text) => {
    var msg = new SpeechSynthesisUtterance();
    msg.text = text;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(msg);
  };

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const handleClickOnMessage = async (message) => {
    handleSend(message.text);
  };

  const handleKeyboardClick = () => {
    setShowInput((prev) => !prev);
  };

  const handleCommandExecutionApiCall = async (commandName) => {
    handleUserTyping();

    console.log("Check the Command Execution API Call", { commandName });

    const response = await chatWithOpenAi(commandName, chatSession);
    if (response) {
      const messagesToAppend = [];

      // Error branch: show error styled message with retry
      if (response.error) {
        messagesToAppend.push({
          sender: "bot",
          text:
            response.message ||
            "An error occurred while processing your request. Please try again.",
          timestamp: Date.now(),
          messageType: "error",
          retryUtterance: commandName,
        });
        setVoiceAssistantState({
          ...VoiceAssistantState,
          isListening: false,
        });
      } else {
        // Primary bot reply with any suggestions/items on same object so UI can render chips/table
        if (response.reply || response.suggestions || response.items) {
          messagesToAppend.push({
            ...response,
            sender: "bot",
            text: response?.reply || "",
            timestamp: Date.now(),
            messageType: "text",
          });
          setVoiceAssistantState({
            ...VoiceAssistantState,
            isListening: true,
          });
        }

        // If nextAction is confirm, show a form preview message
        if (response?.nextAction === "confirm" && response?.formState) {
          messagesToAppend.push({
            sender: "bot",
            timestamp: Date.now(),
            messageType: "formPreview",
            formState: response.formState,
          });
        }
      }

      if (messagesToAppend.length > 0) {
        setChatMessages((prev) => [...prev, ...messagesToAppend]);
      }

      if (response.sessionId) {
        setChatSession(response.sessionId);
      }
      console.log("Check the Response : ", response);
    }
  };

  // helpers for dynamic table based on items/itemType in message
  const renderSuggestionLabel = (opt) => {
    if (opt == null) return "";
    if (typeof opt === "string" || typeof opt === "number") return String(opt);
    if (typeof opt === "object") {
      return (
        opt.label ||
        opt.name ||
        opt.title ||
        opt.description ||
        opt.text ||
        opt.value ||
        "Option"
      );
    }
    return String(opt);
  };

  const toTitleCase = (str = "") =>
    str
      .replace(/[_-]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^\w|\s\w/g, (m) => m.toUpperCase());

  const buildColumnsForItemType = (itemType, itemsSample = []) => {
    const keys =
      itemsColumnsByType[itemType] ||
      (itemsSample.length > 0 ? Object.keys(itemsSample[0]) : []);

    return keys.map((key) => ({
      field: key,
      headerName: toTitleCase(key),
      sortable: false,
      resizable: false,
      width: 180,
      renderCell: (rowData) => {
        const value = rowData?.row?.[key];
        return (
          <Tooltip title={String(value ?? "")}>
            <p style={{ height: "100%", alignContent: "center", margin: 0 }}>
              {String(value ?? "")}
            </p>
          </Tooltip>
        );
      },
    }));
  };

  const renderValuePretty = (val) => {
    if (val == null) return "";
    if (
      typeof val === "string" ||
      typeof val === "number" ||
      typeof val === "boolean"
    )
      return String(val);
    try {
      return JSON.stringify(val);
    } catch (e) {
      return String(val);
    }
  };

  return (
    <div>
      {showArrowBack && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingX: 2,
          }}
        >
          <IconButton>
            <ArrowBackIcon />
          </IconButton>
          <IconButton>
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* Main Content */}
      {chatMessages?.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          {/* Profile Avatar */}
          <Avatar
            src={voiceAssistantImg}
            alt="Helper"
            sx={{ width: 80, height: 80, mb: 2 }}
          />
          <Typography variant="h6" fontWeight="bold">
            How may I be of help to you?
          </Typography>

          {
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, color: "gray" }}>
                QUICK SUGGESTIONS
              </Typography>

              {/* Quick Suggestion Buttons */}
              <Stack spacing={2} sx={{ mt: 2, width: "100%", maxWidth: 300 }}>
                <Button
                  onClick={async () => {
                    handleSend("Search for a property");
                    await delay(1000);
                  }}
                  variant="outlined"
                  fullWidth
                >
                  Search for a property
                </Button>
                <Button
                  onClick={() => {
                    handleSend("Create a New property");
                  }}
                  variant="outlined"
                  fullWidth
                >
                  Create a New property
                </Button>
                <Button
                  onClick={() => {
                    handleSend("Create a New project");
                  }}
                  variant="outlined"
                  fullWidth
                >
                  Create a New project
                </Button>
                <Button
                  onClick={() => {
                    handleSend("Create a Job");
                  }}
                  variant="outlined"
                  fullWidth
                >
                  Create a Job
                </Button>
                <Button
                  onClick={() => {
                    handleSend("Create a Task");
                  }}
                  variant="outlined"
                  fullWidth
                >
                  Create a Task
                </Button>
              </Stack>
            </>
          }
        </Box>
      )}

      {(chatMessages?.length > 0 || !isMobile || agentProcessing) && (
        <div
          className="chat-container"
          key={`chat-container-${chatMessages.length}`}
        >
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 1 }}
          >
            <Button variant="text" size="small" onClick={clearSession}>
              Clear session
            </Button>
          </Box>
          {/* Chat Body */}
          <div
            className="chat-body"
            key={`chat-body-${chatMessages.length}`}
            ref={chatBodyRef}
            // style={{ overflowY: "auto", maxHeight: "700px" }}
            // style={{ maxWidth: "400px", maxHeight: "70vh" }}
            onScroll={handleScroll}
          >
            {chatMessages.map((message, index) => (
              <>
                {message.messageType === "text" && (
                  <div
                    key={`chat-message-${index}`}
                    className={`chat-message ${
                      message.sender === "bot"
                        ? "chat-message-bot"
                        : "chat-message-user"
                    }`}
                    onClick={
                      message.clickEnabled
                        ? () => handleClickOnMessage(message)
                        : null
                    }
                  >
                    <Paper className="chat-bubble" key={`chat-bubble-${index}`}>
                      <span
                        className="chat-message-text"
                        key={`chat-message-text-${index}`}
                        style={{
                          color: message.clickEnabled ? "#007bff" : "inherit",
                          textDecoration: message.clickEnabled
                            ? "underline"
                            : "none",
                          cursor: message.clickEnabled ? "pointer" : "default",
                        }}
                      >
                        {message.text}
                      </span>
                      <span
                        className="chat-message-timestamp"
                        key={`chat-message-timestamp-${index}`}
                      >
                        {new Date(message.timestamp).toLocaleString()}
                      </span>
                    </Paper>
                  </div>
                )}

                {Array.isArray(message.suggestions) &&
                  message.suggestions.length > 0 && (
                    <div
                      key={`chat-suggestions-${index}`}
                      className={`chat-message ${
                        message.sender === "bot"
                          ? "chat-message-bot"
                          : "chat-message-user"
                      }`}
                    >
                      <Paper
                        className="chat-bubble"
                        key={`chat-suggestions-bubble-${index}`}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, color: "gray" }}
                        >
                          Suggestions
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          {message.suggestions.map((opt, idx) => {
                            const label = renderSuggestionLabel(opt);
                            return (
                              <Button
                                key={`suggestion-${index}-${idx}`}
                                variant="outlined"
                                size="small"
                                onClick={() => handleSend(idx + 1)}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 999,
                                }}
                                title={
                                  typeof opt === "object"
                                    ? JSON.stringify(opt)
                                    : label
                                }
                              >
                                {idx + 1}. {label}
                              </Button>
                            );
                          })}
                        </Stack>
                      </Paper>
                    </div>
                  )}

                {message.messageType === "table" && (
                  <div
                    key={`chat-message-${index}`}
                    className="chat-message"
                    style={{ width: "100%", overflowX: "auto" }}
                  >
                    <DataGridTable
                      checkboxSelection={false}
                      key={index}
                      // getRowHeight={() => "auto"}
                      pagination={false}
                      getRowHeight={() => 50}
                      columns={columns({
                        navigate: navigate,
                        propertiesList: propertiesList,
                        setPropertyData,
                      })}
                      data={message.data}
                      defineRowId={(row) => {
                        return row.propertyId;
                      }}
                      page
                      totalCount={totalCount}
                      onDeleteSelection={() => {}}
                      showToolbar={false}
                      onRowClick={(row) => {
                        const selectedProperty = propertiesList.find(
                          (property) => property.propertyId === row.id
                        );

                        dispatch(setPropertyData(selectedProperty));

                        handleClickOnMessage({
                          text: selectedProperty.propertyName,
                          type: VoiceAssistantFlowState.command,
                        });
                      }}
                      onPaginationChange={() => {}}
                      isLoading={() => {}}
                      onSelect={(selected) => {
                        console.log("selected clicked", selected);
                      }}
                      selectedRows={() => {}}
                    />
                  </div>
                )}

                {Array.isArray(message.items) && message.items.length > 0 && (
                  <div
                    key={`chat-items-${index}`}
                    className="chat-message"
                    style={{ width: "100%", overflowX: "auto" }}
                  >
                    <DataGridTable
                      key={`dynamic-table-${index}`}
                      getRowHeight={() => 50}
                      columns={buildColumnsForItemType(
                        message.itemType,
                        message.items
                      )}
                      data={message.items}
                      defineRowId={(row) => {
                        const type = message.itemType || "";
                        const singular = type.endsWith("s")
                          ? type.slice(0, -1)
                          : type;
                        return (
                          row.id ||
                          row[`${singular}Id`] ||
                          row.propertyId ||
                          row.projectId ||
                          row.jobId ||
                          row._id
                        );
                      }}
                      pagination={
                        (message.items?.length || 0) >
                        (MOBILE_MAX_LISTED_RESULT || 10)
                      }
                      page
                      totalCount={message.items?.length || 0}
                      onDeleteSelection={() => {}}
                      showToolbar={false}
                      onRowClick={(row) => {
                        // Optional: echo selection back to chat
                        handleClickOnMessage({
                          text:
                            Object.values(row?.row || {})[0] || "Selected row",
                          type: message.itemType,
                        });
                      }}
                      onPaginationChange={() => {}}
                      isLoading={() => {}}
                      onSelect={() => {}}
                      selectedRows={() => {}}
                    />
                  </div>
                )}

                {message.messageType === "html" && (
                  <div
                    key={`chat-message-${index}`}
                    className={`chat-message ${
                      message.sender === "bot"
                        ? "chat-message-bot"
                        : "chat-message-user"
                    }`}
                    onClick={
                      message.clickEnabled
                        ? () => handleClickOnMessage(message)
                        : null
                    }
                  >
                    <Paper className="chat-bubble" key={`chat-bubble-${index}`}>
                      <span
                        className="chat-message-text"
                        key={`chat-message-text-${index}`}
                        style={{
                          color: message.clickEnabled ? "#007bff" : "inherit",
                          textDecoration: message.clickEnabled
                            ? "underline"
                            : "none",
                          cursor: message.clickEnabled ? "pointer" : "default",
                        }}
                        {...{
                          dangerouslySetInnerHTML: { __html: message.text },
                        }}
                      ></span>
                      <span
                        className="chat-message-timestamp"
                        key={`chat-message-timestamp-${index}`}
                      >
                        {new Date(message.timestamp).toLocaleString()}
                      </span>
                    </Paper>
                  </div>
                )}

                {message.messageType === "formPreview" && (
                  <div
                    key={`chat-message-${index}`}
                    className={`chat-message ${
                      message.sender === "bot"
                        ? "chat-message-bot"
                        : "chat-message-user"
                    }`}
                  >
                    <Paper className="chat-bubble" key={`chat-bubble-${index}`}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Please confirm the details:
                      </Typography>
                      <Box
                        component="div"
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "auto 1fr",
                          columnGap: 1,
                          rowGap: 0.5,
                        }}
                      >
                        {Object.entries(message.formState || {}).map(
                          ([k, v]) => (
                            <React.Fragment key={`fp-${index}-${k}`}>
                              <Typography
                                variant="body2"
                                sx={{ color: "text.secondary" }}
                              >
                                {startCase(k)}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {renderValuePretty(v)}
                              </Typography>
                            </React.Fragment>
                          )
                        )}
                      </Box>
                    </Paper>
                  </div>
                )}

                {message.messageType === "error" && (
                  <div
                    key={`chat-message-${index}`}
                    className="chat-message chat-message-bot"
                  >
                    <Paper
                      className="chat-bubble"
                      elevation={3}
                      sx={{
                        border: "1px solid #f44336",
                        background: "#fdecea",
                      }}
                    >
                      <Alert
                        severity="error"
                        variant="outlined"
                        sx={{ p: 0, border: "none", background: "none" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {message.text}
                        </Typography>
                        {message.retryUtterance && (
                          <Box sx={{ mt: 1 }}>
                            <button
                              className="primaryButton"
                              style={{ backgroundColor: "red", width: "5rem" }}
                              onClick={() =>
                                handleCommandExecutionApiCall(
                                  message.retryUtterance
                                )
                              }
                            >
                              Retry
                            </button>
                          </Box>
                        )}
                      </Alert>
                      <span className="chat-message-timestamp">
                        {new Date(message.timestamp).toLocaleString()}
                      </span>
                    </Paper>
                  </div>
                )}
              </>
            ))}

            {/* Loading (agent processing) bubble */}
            {agentProcessing && (
              <div className="chat-message chat-message-bot" key="loader-msg">
                <Paper
                  className="chat-bubble"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Processing request. Please wait...
                  </Typography>
                </Paper>
              </div>
            )}
            <div ref={endOfMessagesRef} />
          </div>

          {/* Typing Indicator */}
          <div
            className={`chat-typing-indicator ${isMobile ? "mobile" : ""} ${
              isTyping ? "visible" : ""
            }`}
            key="typing-indicator"
          >
            <div className="typing-dot"></div>
            <div className="typing-dot"></div>
            <div className="typing-dot"></div>
          </div>
        </div>
      )}

      {/* Floating Keyboard Button */}
      {isMobile && (
        <IconButton
          className="floating-keyboard-button"
          onClick={handleKeyboardClick}
        >
          <img
            src={keyboardImg}
            alt="Keyboard Input"
            className="keyboard-icon"
          />
        </IconButton>
      )}

      {/* Input Section */}
      {(!isMobile || showInput) && (
        <div className={`chat-input ${isMobile ? "mobile" : ""}`}>
          <TextField
            variant="outlined"
            placeholder="Type your message here"
            className={`chat-input-field ${isMobile ? "mobile" : ""}`}
            value={inputMessage}
            fullWidth={!isMobile}
            onKeyDown={keyPress}
            onChange={(e) => setInputMessage(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleInputSend}>
                    <SendIcon className="chat-send-icon" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* <div >
              {VoiceAssistantState.isListening && <ListeningAnimation />}
            </div> */}
          {!isMobile && (
            <IconButton
              className="chat-voice-button"
              onClick={() => {
                setVoiceAssistantState({
                  ...VoiceAssistantState,
                  isListening: !VoiceAssistantState.isListening,
                });
              }}
            >
              {VoiceAssistantState.isListening && (
                <ListeningAnimation isMobile={isMobile} />
              )}
              <img
                src={voiceAssistantImg}
                alt="Voice Assistant"
                className="chat-voice-icon"
              />
            </IconButton>
          )}
        </div>
      )}

      {chatMessages?.length === 0 && listening && (
        <>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {timeElapsed}: {typewriterText}
          </Typography>
        </>
      )}
    </div>
  );
};

export default VoiceAssistantLayout;
